import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trash, FileText, ArrowsLeftRight, Truck, Plus } from "@phosphor-icons/react";
import { CALC } from "@/constants/testIds";
import { useT } from "@/lib/i18n";
import { fmtMoney, fmtRPM } from "@/lib/defaults";

export const HistoryTable = ({
  lang,
  loads,
  loading = false,
  onDelete,
  onClearAll,
  onCompare,
  onExportRow,
  onFocusCalculator,
}) => {
  const t = useT(lang);
  const [selected, setSelected] = useState([]);

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleCompare = () => {
    onCompare(selected);
  };

  return (
    <Card
      data-testid="history-card"
      className="rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-900 shadow-[0_2px_10px_rgba(15,23,42,0.04)] overflow-hidden"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-5 md:p-6 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-700 dark:text-slate-200">
            {t("history")}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("selectToCompare")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid={CALC.btnCompare}
            variant="default"
            size="sm"
            className="bg-blue-700 hover:bg-blue-800 rounded-lg"
            onClick={handleCompare}
            disabled={selected.length !== 2}
          >
            <ArrowsLeftRight size={16} weight="bold" className="me-1.5" />
            {t("compareSelected")} ({selected.length}/2)
          </Button>
          {loads.length > 0 && (
            <Button
              data-testid={CALC.btnClearHistory}
              variant="outline"
              size="sm"
              className="rounded-lg border-slate-300 text-slate-700"
              onClick={onClearAll}
            >
              {t("clearAll")}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div data-testid="loads-skeleton" className="p-5 md:p-6 space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton h-11 rounded-lg" style={{ animationDelay: `${i * 120}ms` }} />
          ))}
        </div>
      ) : loads.length === 0 ? (
        <div
          data-testid="empty-state-history"
          className="px-6 py-14 md:py-16 flex flex-col items-center justify-center text-center"
        >
          <div className="relative w-24 h-24 mb-5 opacity-90">
            <div
              aria-hidden
              className="absolute inset-0 rounded-full blur-2xl opacity-40"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(16,185,129,0.35), transparent 70%)",
              }}
            />
            <div className="relative w-24 h-24 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 grid place-items-center">
              <Truck size={44} weight="duotone" className="text-emerald-500 flip-x" />
            </div>
          </div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">
            No loads screened yet
          </h3>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400 max-w-sm">
            Save your first load to build a searchable history and unlock
            weekly analytics. It takes 3 seconds.
          </p>
          <Button
            data-testid="btn-empty-analyze-first"
            onClick={() => {
              if (typeof onFocusCalculator === "function") onFocusCalculator();
            }}
            className="btn-neon mt-6 h-11 px-5 rounded-xl text-sm"
          >
            <Plus size={16} weight="bold" className="me-1.5" />
            Analyze Your First Load
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid={CALC.historyTable}>
            <TableHeader>
              <TableRow className="bg-slate-50/60 hover:bg-slate-50/60">
                <TableHead className="w-10"></TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                  {t("route")}
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-slate-500 text-end">
                  {t("totalMiles")}
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-slate-500 text-end">
                  {t("rate")}
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-slate-500 text-end">
                  {t("netProfit")}
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-slate-500 text-end">
                  {t("profitPerMile")}
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                  {t("status")}
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-slate-500 text-end">
                  {t("actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loads.map((l) => (
                <TableRow
                  key={l.id}
                  data-testid={`${CALC.historyRow}-${l.id}`}
                  className="hover:bg-slate-50/50"
                >
                  <TableCell>
                    <Checkbox
                      data-testid={`${CALC.historyCheckbox}-${l.id}`}
                      checked={selected.includes(l.id)}
                      onCheckedChange={() => toggle(l.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-slate-900">
                      {l.pick_city} → {l.delivery_city}
                    </div>
                    <div className="text-xs text-slate-500">
                      {l.commodity} ·{" "}
                      {new Date(l.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-end tabular text-sm">
                    {Math.round(l.total_miles).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-end tabular text-sm">
                    {fmtMoney(l.rate)}
                  </TableCell>
                  <TableCell
                    className={`text-end tabular font-semibold text-sm ${l.net_profit >= 0 ? "text-emerald-700" : "text-red-600"}`}
                  >
                    {fmtMoney(l.net_profit)}
                  </TableCell>
                  <TableCell className="text-end tabular text-sm">
                    {fmtRPM(l.profit_per_mile)}
                  </TableCell>
                  <TableCell>
                    {l.is_profitable ? (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 rounded-md text-[10px] uppercase tracking-wide">
                        Profitable
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border border-red-200 rounded-md text-[10px] uppercase tracking-wide">
                        Not Profitable
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-blue-700"
                        onClick={() => onExportRow(l)}
                        title={t("exportPdf")}
                      >
                        <FileText size={16} weight="bold" />
                      </Button>
                      <Button
                        data-testid={`${CALC.historyDelete}-${l.id}`}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-red-600"
                        onClick={() => onDelete(l.id)}
                        title={t("delete")}
                      >
                        <Trash size={16} weight="bold" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
};
